export interface IEventSubscription {
    unsubscribe(): void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Listener<TArgs extends any[]> = (...args: TArgs) => void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class EventObject<TArgs extends any[]> {
    private readonly listeners: Array<Listener<TArgs>> = [];

    public emit(...args: TArgs): void {
        for (const listener of this.listeners) {
            listener(...args);
        }
    }

    public subscribe(listener: Listener<TArgs>): IEventSubscription {
        this.listeners.push(listener);
        return {
            unsubscribe: () => {
                const index = this.listeners.indexOf(listener);
                if (index >= 0) {
                    this.listeners.splice(index, 1);
                }
            },
        };
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AsyncListener<TArgs extends any[]> = (...args: TArgs) => Promise<void>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class AsyncEventObject<TArgs extends any[]> {
    private readonly listeners: Array<AsyncListener<TArgs>> = [];

    public async emit(...args: TArgs): Promise<void> {
        const l = [...this.listeners];
        for (const listener of l) {
            try {
                await listener(...args);
            } catch {
                // ignore error
            }
        }
    }

    public subscribe(listener: AsyncListener<TArgs>): IEventSubscription {
        this.listeners.push(listener);
        return {
            unsubscribe: () => {
                const index = this.listeners.indexOf(listener);
                if (index >= 0) {
                    this.listeners.splice(index, 1);
                }
            },
        };
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createEvent<TArgs extends any[]>(): EventObject<TArgs> {
    return new EventObject<TArgs>();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createAsyncEvent<TArgs extends any[]>(): AsyncEventObject<TArgs> {
    return new AsyncEventObject<TArgs>();
}
